import styled from 'styled-components';
import BaseLocationField, {
  Styled as LocationFieldClasses,
} from '@opentripplanner/location-field';
import { DESKTOP_MIN_BREAK, GTM_DATA_ATTRIBUTE } from '#/shared/constants';
import { InverseClearButton } from '#/shared/components/Styled';
import BaseLocationResultIcon from '#/shared/components/LocationResultIcon';
import BaseGeocodedFeatureIcon from '#/shared/components/GeocodedFeatureIcon';
import BaseAlertIcon from '#/shared/components/GeneratedIcons/trimet/AlertSolid';
import { focusCssRule } from '#/shared/css/focus';
import { CurrentLocationMarker } from '#/map/components/CurrentLocationOverlay';
import BaseCloseIcon from '#/shared/components/GeneratedIcons/generic/Close';

const BUTTON_OFFSET = '44px';

const BASE_ICON_PROPS = {
  role: 'presentation',
  width: 16,
  height: 16,
};

export const OmniboxBody = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.inverseBackgroundColor2};
  color: ${props => props.theme.inverseFontColor1};
  max-width: ${DESKTOP_MIN_BREAK};
  padding: 15px;
  overflow-y: auto;
`;

export const Panel = styled.div`
  margin: 20px 0 0;
`;

export const PanelSubheading = styled.div`
  font-weight: 600;
  padding-left: 5px;
  margin: 0 0 5px;
`;

export const PanelInfo = styled.div`
  margin: 1px 4px;
  color: ${props => props.theme.inverseFontColor2};
  font-style: italic;
  line-height: 1.25;
`;

export const CurrentLocationBadge = styled(CurrentLocationMarker).attrs({
  size: 11,
})`
  margin: 3px 0 0 2px;
`;

export const GeocodedFeatureIcon = styled(BaseGeocodedFeatureIcon).attrs(
  props => ({
    ...BASE_ICON_PROPS,
    fill: props.theme.mainColor,
    isResult: true,
  })
)``;

export const OutOfBoundsWarning = styled.div`
  background-color: ${props => props.theme.inverseBackgroundColor2};
  padding: 10px;
  margin-bottom: 5px;
  display: flex;
`;

export const OutOfBoundsWarningTextColumn = styled.div`
  flex: 1 1 auto;
  margin-left: 10px;
`;

export const OutOfBoundsWarningIconColumn = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

export const OutOfBoundsIcon = styled(BaseAlertIcon).attrs(props => ({
  width: 24,
  height: 24,
  title: 'Out of Bounds',
  fill: props.theme.inverseFontColor1,
}))``;

export const SearchHint = styled.div`
  padding-left: ${BUTTON_OFFSET};
  color: ${props => props.theme.inverseFontColor2};
  font-style: italic;
`;

export const ButtonListItem = styled(InverseClearButton).attrs(props => ({
  role: 'link',
  [GTM_DATA_ATTRIBUTE]: props.dataGtm,
}))`
  display: flex;
  align-items: start;
  width: 100%;
  padding: 3px 8px;
  margin-top: 8px;
  opacity: 1;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    padding: 3px 0 3px 8px;
  }

  @media (any-hover: hover) {
    &:hover {
      background-color: transparent;
      opacity: ${props => props.theme.inactiveOpacity};
    }
  }
  &:focus,
  &:active {
    background-color: transparent;
  }
`;

export const ButtonListIcon = styled(BaseLocationResultIcon).attrs(props => ({
  ...BASE_ICON_PROPS,
  fill: props.type === 'stop' ? props.theme.inverseFontColor1 : null,
}))`
  position: relative;
  flex: 0 0 20px;
  align-self: center;
`;

export const ButtonListName = styled.div`
  margin-left: 10px;
  font-size: 1.2rem;
`;

export const ButtonListHint = styled.span`
  color: ${props => props.theme.primaryFontColorReverse};
  font-size: 0.85em;
  margin-left: 10px;
  white-space: nowrap;
`;

export const CloseIcon = styled(BaseCloseIcon).attrs(props => ({
  role: 'presentation',
  fill: props.theme.secondaryFontColor,
  width: 24,
  height: 24,
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.theme.inactiveOpacity};

  @media (any-hover: hover) {
    &:hover {
      cursor: pointer;
      opacity: 1;
    }
  }
`;

export const LocationField = styled(BaseLocationField)`
  background-color: ${props => props.theme.secondaryBackgroundColor};
  border-radius: ${props => props.theme.buttonRadius};
  display: flex;
  flex-direction: column;

  ${LocationFieldClasses.FormGroup} {
    display: block;
    padding: 0;
    margin: 0;
  }

  ${LocationFieldClasses.InputGroup} {
    border: 0;
    display: flex;

    & > *:not(:first-child) {
      border-radius: 0;
      border-bottom: 2px solid ${props => props.theme.inverseBackgroundColor1};
      margin-bottom: -2px;
    }
  }

  ${LocationFieldClasses.InputGroupAddon} {
    display: flex;
    align-items: center;
    cursor: default;
    padding: 0 3px;

    ${LocationFieldClasses.Button} {
      align-items: center;
      justify-content: center;
    }
  }

  ${LocationFieldClasses.OptionIconContainer} {
    margin: 0;
    display: flex;
    align-items: center;
  }

  ${LocationFieldClasses.Input} {
    font-family: inherit;
    background-color: transparent;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: ${props => props.theme.secondaryFontColor};
    font-weight: 500;
    flex: auto;
    font-size: 2.5em;
    border-radius: 0;
    padding: 5px 3px;
    min-width: 0;

    /* Adjust focus indicator when X button isn't present */
    &:last-child {
      margin-right: 4px;
    }

    @media (min-width: ${DESKTOP_MIN_BREAK}) {
      font-size: 2em;
    }

    &::placeholder {
      color: ${props => props.theme.inverseFontColor2};
      font-size: 1.5rem;
      vertical-align: middle;
      line-height: 2;
    }

    ${focusCssRule({ type: 'default' })}
  }

  ${LocationFieldClasses.StaticMenuItemList} {
    flex: 1 1 auto;
    width: 100%;
    border: 0;
    border-radius: 0 0 5px 5px;
    margin: 0;
    padding: 0;
    background-color: transparent;

    &[aria-hidden='true'] {
      display: none;
    }
  }

  ${LocationFieldClasses.MenuItemHeader} {
    display: none;
  }

  ${LocationFieldClasses.MenuItemA} {
    color: ${props => props.theme.mainColor};
    user-select: none;
    padding: 3px 10px;
    opacity: ${props => props.theme.inactiveOpacity};
    &:focus,
    &:active {
      background-color: ${props => props.theme.activeColor};
    }
    @media (any-hover: hover) {
      &:hover {
        background-color: ${props => props.theme.activeColor};
        opacity: 1;
      }
    }
  }

  ${LocationFieldClasses.OptionContainer} {
    display: flex;
  }

  ${LocationFieldClasses.OptionContent} {
    font-size: 1.2rem;
    margin-left: 13px;
    overflow: visible;
    white-space: normal;
    line-height: 1;
  }
  ${LocationFieldClasses.OptionSubTitle} {
    color: ${props => props.theme.mainColor};
  }
  ${LocationFieldClasses.MenuItemLi} {
    cursor: pointer;
    margin: 10px 0;

    &:focus,
    &:active {
      background-color: transparent;
    }

    @media (any-hover: hover) {
      &:hover {
        background-color: transparent;
      }
    }
  }
`;
